import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC",
];

export default function ContactForm({ onSubmit }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.full_name ||
      !form.email ||
      !form.phone ||
      !form.city ||
      !form.state
    ) {
      toast.error("Please complete all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit}>
      <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-3">
        Final step
      </p>
      <h1 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0A192F] tracking-tight">
        Where can we reach you?
      </h1>
      <p className="mt-3 text-slate-600 leading-relaxed">
        Approved attorneys matched to your case will use this contact
        information to reach out about your matter.
      </p>

      <div className="mt-8 space-y-5 bg-white border border-slate-200 rounded-xl p-6">
        <Field
          label="Full Name"
          testId="contact-name"
          value={form.full_name}
          onChange={update("full_name")}
          placeholder="Jane Doe"
        />
        <div className="grid sm:grid-cols-2 gap-5">
          <Field
            label="Email"
            type="email"
            testId="contact-email"
            value={form.email}
            onChange={update("email")}
            placeholder="jane@example.com"
          />
          <Field
            label="Phone"
            type="tel"
            testId="contact-phone"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(555) 555-5555"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field
            label="City"
            testId="contact-city"
            value={form.city}
            onChange={update("city")}
            placeholder="Los Angeles"
          />
          <div>
            <Label
              htmlFor="state-select"
              className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500"
            >
              State
            </Label>
            <Select value={form.state} onValueChange={update("state")}>
              <SelectTrigger
                id="state-select"
                data-testid="contact-state"
                className="mt-2 rounded-lg border-slate-300 focus:ring-2 focus:ring-[#0A192F]/20"
              >
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`state-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Button
        type="submit"
        data-testid="contact-submit-btn"
        disabled={submitting}
        className="mt-6 w-full bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg py-6 text-base"
      >
        {submitting ? "Submitting…" : "Submit Case File"}
      </Button>

      <p className="mt-4 text-xs text-slate-500 text-center leading-relaxed">
        By submitting, you agree your information will be shared with approved
        attorneys matched to your case. LexIntake is not a law firm and does
        not provide legal advice.
      </p>
    </form>
  );
}

function Field({ label, testId, value, onChange, placeholder, type = "text" }) {
  const id = `field-${testId}`;
  return (
    <div>
      <Label
        htmlFor={id}
        className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500"
      >
        {label}
      </Label>
      <Input
        id={id}
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-2 rounded-lg border-slate-300 focus-visible:ring-2 focus-visible:ring-[#0A192F]/20"
      />
    </div>
  );
}
